// Options for the picture the in-app bug reporter draws of the app.
//
// The reporter does not ask the operating system for a screenshot. It re-draws
// the page into a picture with the `dom-to-image-more` package. That package
// copies every element and its styles, but it does NOT copy how far an element
// has been scrolled, so in the copy every scrollable area starts at its
// beginning again. On the date strip that meant the picture showed the oldest
// week the strip holds, with the selected day off to the right of the drawing
// and its highlight nowhere to be seen (#4868).
//
// The fix shifts the CHILDREN of a scrolled container in the copy, by exactly
// as far as the real container is scrolled, on both axes. Two reasons it is the
// children and not the container itself:
//
//   - The package styles a node AFTER it calls us for that node, but its
//     children are already finished by then. So a style we write on a child
//     sticks, while one we write on the container can be replaced.
//   - The container keeps the `overflow` style the package copied, and that is
//     what trims the shifted content at the edge of the box, which is what real
//     scrolling looks like.
//
// A `transform` is used for the shift rather than a negative margin because a
// transform takes no part in layout, so it behaves the same whether the
// container lays its children out as a block, as a flex row or as a grid. A
// negative margin on the first child would move only that child inside a grid,
// and would overwrite the margin the package copied. The one thing a transform
// cannot shift is a bare piece of text or a plain `display: inline` child; no
// scrollable area in this app has those as direct children.
//
// The vertical axis used to be corrected somewhere else entirely: the reporter
// moved the live page's own scroll box before the capture and put it back
// afterwards, which covered whole pages and nothing else. So a list inside a
// modal or a popover was drawn from its top however far the user had scrolled
// it. Both axes of every scrolled container are now corrected here instead, so
// there is one correction rather than two, and nothing on the screen the user
// is looking at is touched while the picture is drawn.

/** The smallest bit of a cloned element this file needs to write a shift onto. */
export interface CloneStyleTarget {
  style: { transform: string };
}

/** The smallest bit of a cloned element this file needs to walk. */
export interface CloneElement extends CloneStyleTarget {
  children?: ArrayLike<unknown>;
}

export interface BugReportCaptureOptionsInput {
  /** The element to leave out of the picture — the bug-report button itself. */
  excludeElement: unknown;
  width: number;
  height: number;
}

/**
 * The `transform` to put on a child of a scrolled container, or `undefined`
 * when the container is not scrolled and the child should be left alone.
 *
 * `existingTransform` is whatever the package already copied onto the child.
 * The shift is put in FRONT of it: the leftmost transform in the list is
 * applied last, so the shift happens in the container's own frame, after
 * whatever the child was already doing to itself.
 */
export function composeScrollCompensationTransform(
  scrollLeft: number,
  scrollTop: number,
  existingTransform: string
): string | undefined {
  // an unscrolled container needs no help; leave its children exactly as copied
  if (!scrollLeft && !scrollTop) return undefined;

  const shift = `translate(${-scrollLeft}px, ${-scrollTop}px)`;

  // 'none' and '' both mean "no transform", and neither may be pasted after the
  // shift: `translate(...) none` is not a valid transform list, and a browser
  // drops the whole declaration without saying anything, losing the shift.
  const kept = existingTransform && existingTransform !== 'none' ? existingTransform : '';

  return kept ? `${shift} ${kept}` : shift;
}

/** Everything the bug reporter hands to `domToImage.toCanvas`. */
export function buildBugReportCaptureOptions(input: BugReportCaptureOptionsInput) {
  return {
    filter: (element: unknown) => element !== input.excludeElement,
    width: input.width,
    height: input.height,
    // The package calls this for every node it copies, once before the children
    // are copied and once after. Anything thrown in here fails the whole
    // capture, so every step guards rather than assumes.
    adjustClonedNode: (node: unknown, clone: unknown, afterChildren: boolean) => {
      if (!afterChildren) return;

      const scrolledNode = node as { scrollLeft?: unknown; scrollTop?: unknown } | null;
      const scrollLeft = typeof scrolledNode?.scrollLeft === 'number' ? scrolledNode.scrollLeft : 0;
      const scrollTop = typeof scrolledNode?.scrollTop === 'number' ? scrolledNode.scrollTop : 0;

      const children = (clone as CloneElement | null)?.children;
      if (!children) return;

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as CloneStyleTarget | null;
        if (!child || typeof child.style?.transform !== 'string') continue;

        const transform = composeScrollCompensationTransform(scrollLeft, scrollTop, child.style.transform);
        if (transform) child.style.transform = transform;
      }
    },
  };
}
